import { TabContext, TabPanel } from "@mui/lab";
import ClearIcon from '@mui/icons-material/Clear';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Card, CardContent, CardHeader, Grid, IconButton, Drawer, Tab, Tabs, Tooltip, 
  Typography, useMediaQuery, Skeleton, Stack, Button, alpha, useTheme} from "@mui/material";
import { ListControllerProps, useGetMainList, useRecordContext } from "ra-core";
import { FC, useEffect, useMemo, useState } from "react";
import DatabaseNameField from "~/components/databases/DatabaseNameField";
import { FunctionField, TextField } from "~/components/field";
import { Datagrid } from "~/components/list/datagrid";
import { RecPatch } from "~/dataProvider/tools";
import { DatabaseResponseDto as DatabaseDto } from "~/gen/api/backup";
import { ClientAccessPolicyDto, ClientAccessPolicyResponseDto, DatabaseResponseDto, FileEncryptionTemplateDto, FileTemplateKind } from "~/gen/api/encrypt";
import { ClientManageDto, ClientType } from "~/gen/api/user";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import WithSave from "~/controller/WithSave";
import ReadonlyField from "~/components/input/ReadonlyField";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { SearchAndAdd, SearchAndAddProps } from "~/components/SearchAndAdd";



interface AssignClientDatabaseFileTemplateProps {
  open: boolean;
  onClose: () => void;
  client: ClientManageDto|null;
}

type TabVal = 'database' | 'file-template'

const AssignClientDatabaseFileTemplate: FC<AssignClientDatabaseFileTemplateProps> = ({
  open,
  onClose,
  client
}) => {
  const matchMW = useMediaQuery('(max-width: 950px)')
  const width = matchMW ? 'calc(100vw - 50px)' : 900
  const assignmentDto : ClientAccessPolicyResponseDto|undefined = useRecordContext() as any

  const initVals = () => ({
    currTab: 'database' as TabVal,
    term1: '',
    term2: '',
    stage: {
      removed: {
        databases: new Set<number>(),
        fileTemplates: new Set<number>(),
      },
      added: {
        databases: [] as DatabaseDto[],
        fileTemplates: [] as FileEncryptionTemplateDto[],
      }
    },
  })
  const [states, setStates] = useState(initVals)
  useEffect(() => {
    if (assignmentDto) {
      setStates(initVals())
    }
  }, [assignmentDto])

  const {databaseLsCtx, fileTmplLsCtx} = useMemo(() => {
    const buildListCtx = (ls: any[]|undefined|null, addedLs: any[], sortKey: string, idKey: string) => {
      const sorted = [
        ...addedLs,
        ...(ls ?? []).sort((a, b) => (a[sortKey] ?? '').toLowerCase() < (b[sortKey] ?? '').toLowerCase() ? -1 : 1)]
      const data = sorted.reduce((acc, d) => {
        acc[d[idKey]] = d
        return acc
      }, {} as any)
      const ids = sorted.map(d => d[idKey])
      return { data, ids, currentSort: { field: 'id', order: 'ASC' } }
    }
    return {
      databaseLsCtx: buildListCtx(assignmentDto?.databaseList, states.stage.added.databases, 'name', 'databaseId') as Partial<ListControllerProps<DatabaseResponseDto&RecPatch>>,
      fileTmplLsCtx: buildListCtx(assignmentDto?.fileTemplateList, states.stage.added.fileTemplates, 'name', 'templateId') as Partial<ListControllerProps<FileEncryptionTemplateDto&RecPatch>>,
    }
  }, [assignmentDto, states.stage.added])
  
  const handleTabsChange = (event: any, newValue: string) => {
    setStates(v => ({...v, currTab: newValue as TabVal}))
  }
  const { data: databaseData, ids: ids1, refetch: refetchDbs } = useGetMainList<DatabaseDto & RecPatch>('databases', pages(), sorts(), {})
  const { data: fileTmplData, ids: ids2, refetch: refetchTmpls } = useGetMainList<FileEncryptionTemplateDto & RecPatch>('file-encrypt-template', pages(), sorts(), {})
  useEffect(() => {
    if (open) {
      refetchDbs()
      refetchTmpls()
    }
  }, [refetchDbs, refetchTmpls, open])

  const remainedOptionsIds = useMemo(() => {
    const assignedDIds = (assignmentDto?.databaseList ?? []).map(d => d.databaseId!)
    const assignedTIds = (assignmentDto?.fileTemplateList ?? []).map(d => d.templateId!)
    return {
      databases: ((ids1 ?? []) as number[]).filter(id => assignedDIds.indexOf(id) === -1),
      fileTemplates: ((ids2 ?? []) as number[]).filter(id => assignedTIds.indexOf(id) === -1),
    }
  }, [assignmentDto, ids1, ids2])

  const databaseOptions = useMemo(() => {
    const excludes = states.stage.added.databases.map(d => d.databaseId!);
    return [...remainedOptionsIds.databases.filter(id => excludes.indexOf(id) === -1), ...states.stage.removed.databases]
      .flatMap(id => {
        const d = databaseData && databaseData[id]
        return d ? [d] : []
      }).sort((a, b) => (a.name ?? '').toLowerCase() < (b.name ?? '').toLowerCase() ? -1 : 1)
  }, [databaseData, remainedOptionsIds, states.stage])
  const fileTmplOptions = useMemo(() => {
    const excludes = states.stage.added.fileTemplates.map(d => d.templateId!);
    return [...remainedOptionsIds.fileTemplates.filter(id => excludes.indexOf(id) === -1), ...states.stage.removed.fileTemplates]
    .flatMap(id => {
      const d = fileTmplData && fileTmplData[id]
      return d ? [d] : []
    }).sort((a, b) => (a.name ?? '').toLowerCase() < (b.name ?? '').toLowerCase() ? -1 : 1)
  }, [fileTmplData, remainedOptionsIds, states.stage])

  const addValue = (x: DatabaseResponseDto|FileEncryptionTemplateDto) => {
    const k = isDb(x) ? 'databases' : 'fileTemplates'
    setStates(v => {
      if (v.stage.removed[k].has(getId2(x))) {
        v.stage.removed[k].delete(getId2(x))
        return {...v, stage: {...v.stage}}
      } else if (remainedOptionsIds[k].every(id => id !== getId2(x)) || v.stage.added[k].some((d: DatabaseResponseDto | FileEncryptionTemplateDto) => getId2(d) === getId2(x))) {
        return v
      } else {
        return {...v, 
          stage: {...v.stage,
            added: {...v.stage.added,
              [k]: [x, ...v.stage.added[k]]
            }
          }
        }
      }
    })
  }

  const addValues = (x: DatabaseResponseDto[]|FileEncryptionTemplateDto[]) => {
    if (x.length === 0) { return }
    const k = isDb(x[0]) ? 'databases' : 'fileTemplates'
    setStates(v => {
      const arr = [] as any[]
      for (const y of x) {
        const id = getId2(y)
        if (v.stage.removed[k].has(id)) { continue; } // only support restore by clicking restore btn
        if (remainedOptionsIds[k].every(id_r => id_r !== id) || v.stage.added[k].some((d: DatabaseResponseDto | FileEncryptionTemplateDto) => getId2(d) === id))
        { continue; }
        arr.push(y)
      }
      return arr.length > 0 ? {...v, stage: {...v.stage, added: {...v.stage.added, [k]: [...arr, ...v.stage.added[k]]}}} : v
    })
  }

  const addValueOrValues = (x: DatabaseResponseDto|FileEncryptionTemplateDto|DatabaseResponseDto[]|FileEncryptionTemplateDto[]) => {
    if (x instanceof Array) {
      addValues(x)
    } else {
      addValue(x)
    }
  }
  
  const removeValue = (d: DatabaseResponseDto|FileEncryptionTemplateDto) => {
    const k = isDb2(d) ? 'databases' : 'fileTemplates'
    setStates(v => {
      const i = v.stage.added[k].findIndex((x: DatabaseResponseDto | FileEncryptionTemplateDto) => getId2(x) === getId2(d))
      if (i > -1) {
        const arr = v.stage.added[k]
        return {...v, 
          stage: {...v.stage,
            added: {...v.stage.added,
              [k]: [...arr.slice(0, i), ...arr.slice(i + 1)]
            }
          }
        }
      } else {
        v.stage.removed[k].add(getId2(d))
        return {...v, stage: {...v.stage}}
      }
    })
  }

  const handleSave = (save: (record: any, callbacks?: any) => void) => () => {
    const databaseIds = new Set([
      ...(assignmentDto?.databaseList ?? []).map(x => x.databaseId!),
      ...(states.stage.added.databases.map(x => x.databaseId!))
    ])
    for (let id of states.stage.removed.databases) {
      databaseIds.delete(id)
    }
    const fileTemplateIds = new Set([
      ...(assignmentDto?.fileTemplateList ?? []).map(x => x.templateId!),
      ...(states.stage.added.fileTemplates.map(x => x.templateId!))
    ])
    for (let id of states.stage.removed.fileTemplates) {
      fileTemplateIds.delete(id)
    }

    const payload : ClientAccessPolicyDto = {
      clientId: client?.clientId,
      databaseIds: [...databaseIds],
      fileTemplateIds: [...fileTemplateIds],
    }

    save(payload, {
      onSuccess: () => {
        onClose()
      }
    })
  }

  const theme = useTheme()
  const rowStyles = (d?: (DatabaseResponseDto|FileEncryptionTemplateDto)&RecPatch) => {
    if (!d) { return {} }
    const k = isDb(d) ? 'databases' : 'fileTemplates'
    if (states.stage.removed[k].has(getId2(d))) {
      return { backgroundColor: alpha(theme.palette.error.light, 0.05) }
    } else if (states.stage.added[k].some((x: DatabaseResponseDto | FileEncryptionTemplateDto) => getId2(x) === getId2(d))) {
      return { backgroundColor: alpha(theme.palette.success.light, 0.05) }
    }
    return {}
  }
  const format = useUserTimeFormat()
  const getDateString = (srcStr?: string|null) => {
    const dateVal = (srcStr || '') as string
    const dateString = dateVal ? format(new Date(dateVal), 'Pp') : ''
    return dateString
  }
  
  return (
    <Drawer anchor="right" PaperProps={{ sx: { p: 2, width, }}}
      onClose={onClose}
      open={open}
    >
      <Card>
        <CardHeader title="Assign Resources" />
        <CardContent>
          <Grid container spacing={4} >
            <Grid item xs={12} >
              <ReadonlyField label="Client Name" name="ClientName" value={client?.clientName || ''} />
            </Grid>
            <Grid item xs={4} >
              <Typography variant="body2">
                {client?.clientType === ClientType.DataDriver ? 'Data Driver'
                  : client?.clientType === ClientType.EncryptionTool ? 'Encryption Tool'
                  : client?.clientType === ClientType.ShareSecretDataDriver ? 'Shared Secret Data Driver'
                  : client?.clientType === ClientType.ShareSecretEncryptionTool ? 'Shared Secret Encryption Tool'
                  : null
                }
              </Typography>
              <Typography variant="caption" component="p" color="success.main">
                {client?.softwareVersion}
              </Typography>
            </Grid>
            <Grid item xs={4} >
              <Typography variant="body2">
                {getDateString(client?.registerAt)}
              </Typography>
              <Tooltip title={client?.r1Username || ''}>
                <Box>
                  <Typography variant="caption">
                    Registered By <Typography variant="caption" color="primary.main">
                      {client?.r1UserFirstName} {client?.r1UserLastName}
                    </Typography>
                  </Typography>                
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={4} >
              <Typography variant="body2">
                {client?.osInfo}<br/>
                <Typography variant="caption">
                  {client?.ipAddress}
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} >
              <TabContext value={states.currTab}>
                <Tabs indicatorColor="secondary" scrollButtons="auto" textColor="secondary" variant="scrollable"
                  onChange={handleTabsChange}
                  value={states.currTab}
                >
                  <Tab value="database" 
                    label={
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <span>Database</span>
                        {!!states.stage.added.databases.length && <Typography variant="caption" color="success.main">+ {states.stage.added.databases.length}</Typography>}
                        {!!states.stage.removed.databases.size && <Typography variant="caption" color="error.main">- {states.stage.removed.databases.size}</Typography>}
                      </Stack>} 
                  />
                  <Tab value="file-template" 
                    label={
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <span>File Template</span>
                        {!!states.stage.added.fileTemplates.length && <Typography variant="caption" color="success.main">+ {states.stage.added.fileTemplates.length}</Typography>}
                        {!!states.stage.removed.fileTemplates.size && <Typography variant="caption" color="error.main">- {states.stage.removed.fileTemplates.size}</Typography>}
                      </Stack>} 
                  />
                </Tabs>
                {!assignmentDto &&
                  <Box paddingTop={2}>
                    <Skeleton height="2.5em" />
                    <Skeleton height="2.5em" />
                    <Skeleton height="2.5em" />
                  </Box>}
                {assignmentDto &&
                  <Box sx={{ '& .MuiTabPanel-root': { px: 'unset' } }}>
                    <TabPanel value="database">
                      <Box paddingBottom={2}>
                        <SearchAndAddDatabase term={states.term1} setTerm={(s) => setStates(v => ({...v, term1:s}))} 
                          options={databaseOptions}
                          getOptionLabel={(x)=>x.name!}
                          onAdd={addValueOrValues}
                          liBoxSx={{ whiteSpace: 'break-spaces', wordBreak: 'break-all'}}
                        />
                      </Box>
                      <Box sx={{ maxHeight: 'calc(100vh - 505px)', overflowY: 'auto'}}>
                        <Datagrid hasBulkActions={false} rowStyle={rowStyles} {...databaseLsCtx}
                          sx={{
                            '& tr div.MuiBox-root': {
                              '& div.encryptedDbTip': { top: theme => theme.spacing(-2) },
                              '& span.encryptedDbTipText': { top: theme => theme.spacing(-2) },
                            },
                          }}
                        >
                          <DatabaseNameField label="Name" />
                          <FunctionField label="Engine" source="engineVersion" sortable={false} 
                            render={(d?: DatabaseResponseDto & RecPatch) => (
                              <Typography component="p" variant="body2" >
                                {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                              </Typography>
                            )}
                          />
                          <FunctionField label="Action" sortable={false}
                            render={(d?: DatabaseResponseDto & RecPatch) => (
                              !states.stage.removed.databases.has(d?.databaseId!) 
                              ? <Tooltip title="Remove Assignment">
                                  <IconButton color="primary" onClick={() => removeValue(d!)}>
                                    <ClearIcon />
                                  </IconButton>
                                </Tooltip> 
                              : <Tooltip title="Restore Assignment">
                                  <IconButton color="primary" onClick={() => addValue(d!)}>
                                    <ReplayIcon />
                                  </IconButton>
                                </Tooltip>
                            )} 
                          />
                        </Datagrid>
                      </Box>
                    </TabPanel>
                    <TabPanel value="file-template">
                      <Box paddingBottom={2}>
                        <SearchAndAddFileTmpl term={states.term2} setTerm={(s) => setStates(v => ({...v, term2:s}))} 
                          options={fileTmplOptions}
                          getOptionLabel={(x)=>x.name!}
                          onAdd={addValueOrValues} 
                          liBoxSx={{ whiteSpace: 'break-spaces', wordBreak: 'break-all'}}
                        />
                      </Box>
                      <Box sx={{ maxHeight: 'calc(100vh - 505px)', overflowY: 'auto'}}>
                        <Datagrid hasBulkActions={false} rowStyle={rowStyles} {...fileTmplLsCtx}>
                          <TextField source="name" label="Template Name" sortable={true} sx={{ textTransform: 'uppercase' }} />
                          <FunctionField source="kind" label="Type" sortable={false} 
                            render={(d?:FileEncryptionTemplateDto&RecPatch) => (
                              d && <Typography variant="body2">{
                                d.kind === FileTemplateKind.CSV ? "CSV File" 
                                : d.kind === FileTemplateKind.FixedWidth ? "Fixed Width File"
                                : d.kind === FileTemplateKind.JsonSample ? "Json File"
                                : d.kind === FileTemplateKind.EDI        ? "EDI File"
                                : ("not supported")
                              }</Typography>
                            )}
                          />
                          <FunctionField label="Action" sortable={false}
                            render={(d?: FileEncryptionTemplateDto & RecPatch) => (
                              !states.stage.removed.fileTemplates.has(d?.templateId!) 
                              ? <Tooltip title="Remove Assignment">
                                  <IconButton color="primary" onClick={() => removeValue(d!)}>
                                    <ClearIcon />
                                  </IconButton>
                                </Tooltip> 
                              : <Tooltip title="Restore Assignment">
                                  <IconButton color="primary" onClick={() => addValue(d!)}>
                                    <ReplayIcon />
                                  </IconButton>
                                </Tooltip>
                            )} 
                          />
                        </Datagrid>
                      </Box>
                    </TabPanel>
                  </Box>}
              </TabContext>
            </Grid>
          </Grid>
        </CardContent>
        <WithSave
          render={({save, saving}) =>
            <Stack direction="row" justifyContent="space-between" padding={2} >
              <Button
                color="primary"
                disabled={saving}
                variant="outlined"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={saving || (
                  states.stage.added.databases.length === 0 && 
                  states.stage.added.fileTemplates.length === 0 && 
                  states.stage.removed.databases.size === 0 && 
                  states.stage.removed.fileTemplates.size === 0
                )}
                variant="contained"
                onClick={handleSave(save)}
              >
                Save
              </Button>
            </Stack>}
        />
      </Card>
    </Drawer>
  )
}

export default AssignClientDatabaseFileTemplate;

function isDb(x: DatabaseDto|FileEncryptionTemplateDto): x is DatabaseDto {
  return (x as DatabaseDto).databaseId !== undefined
}
function isDb2(x: DatabaseResponseDto|FileEncryptionTemplateDto): x is DatabaseResponseDto {
  return (x as DatabaseResponseDto).databaseId !== undefined
}
const getId = (x: DatabaseDto|FileEncryptionTemplateDto) => (isDb(x) ? x.databaseId : x.templateId)!
const getId2 = (x: DatabaseResponseDto|FileEncryptionTemplateDto) => (isDb2(x) ? x.databaseId : x.templateId)!


const SearchAndAddDatabase : FC<SearchAndAddProps<DatabaseResponseDto>> = SearchAndAdd

const SearchAndAddFileTmpl : FC<SearchAndAddProps<FileEncryptionTemplateDto>> = SearchAndAdd

